'use client';

import { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import { MapPinIcon } from '@heroicons/react/24/solid';
import GoogleMap from 'google-maps-react-markers';
import mapStyle from '@/components/PuntosHop/Map/map-style';
import markerStyles from '@/components/PuntosHop/Marker/marker.module.scss';
import PersonalPointer from '@/components/PuntosHop/PersonalPointer';
import useSupercluster from 'use-supercluster';
import env from '@architecture-it/react-env';
import ComoLlegarCta from '../ComoLlegarCta';
import ServiceBadges from '../ServiceBadges';
import { groupSchedules } from '../schedules';
import { isResellerPoint } from '@/utils/hopPuntos';

const geoOptions = {
  enableHighAccuracy: true,
  timeout: 5000,
  maximumAge: 0
};

const mapOptions = {
  panControl: false,
  zoomControl: true,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  overviewMapControl: false,
  rotateControl: false
};

const pointsToClusters = (points) => {
  if (!points || !Array.isArray(points)) return [];
  return points.map((p) => ({
    type: 'Feature',
    properties: {
      cluster: false,
      punto: {
        ...p,
        schedules: (p.schedules || [])
          .sort((a, b) => parseInt(a.day_code) - parseInt(b.day_code))
          .map((s) => ({
            ...s,
            hour_from: s.hour_from?.substring(0, 5) || '',
            hour_to: s.hour_to?.substring(0, 5) || ''
          }))
      }
    },
    geometry: {
      type: 'Point',
      coordinates: [parseFloat(p.lng), parseFloat(p.lat)]
    }
  }));
};

const hasSeller = (point) => isResellerPoint(point);

const EnviosMarker = ({
  id,
  cluster,
  openMarker,
  onClick,
  data,
  searchMethod,
  mapContainerRef,
  mapRevision,
  popupContainer
}) => {
  const isOpen = id === openMarker;
  const isSeller = hasSeller(data);
  const markerRef = useRef(null);
  const popupRef = useRef(null);
  const [popupPosition, setPopupPosition] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setPopupPosition(null);
      return undefined;
    }

    const positionPopup = () => {
      const mapElement = mapContainerRef?.current;
      const markerElement = markerRef.current;
      const popupElement = popupRef.current;
      if (!mapElement || !markerElement || !popupElement) return;

      const mapRect = mapElement.getBoundingClientRect();
      const markerRect = markerElement.getBoundingClientRect();
      const width = Math.max(0, Math.min(320, mapRect.width - 16, window.innerWidth - 16));
      if (!width || !mapRect.height) return;

      const bottomInset = Math.min(112, Math.max(72, mapRect.height * 0.18));
      const safeTop = Math.max(8, mapRect.top + 8);
      const safeBottom = Math.min(window.innerHeight - 8, mapRect.bottom - bottomInset);
      const maxHeight = Math.max(120, safeBottom - safeTop);
      popupElement.style.width = `${width}px`;
      popupElement.style.maxHeight = `${maxHeight}px`;
      const height = popupElement.getBoundingClientRect().height;
      const minLeft = Math.max(8, mapRect.left + 8);
      const maxLeft = Math.max(minLeft, Math.min(window.innerWidth - 8, mapRect.right - 8) - width);
      const left = Math.max(
        minLeft,
        Math.min(markerRect.left + markerRect.width / 2 - width / 2, maxLeft)
      );
      const above = markerRect.top - height - 12;
      const below = markerRect.bottom + 12;
      const proposedTop = above >= safeTop || below + height > safeBottom ? above : below;
      const top = Math.max(safeTop, Math.min(proposedTop, safeBottom - height));

      setPopupPosition({ left, top, width, maxHeight });
    };

    positionPopup();
    window.addEventListener('resize', positionPopup);
    window.addEventListener('scroll', positionPopup, true);
    return () => {
      window.removeEventListener('resize', positionPopup);
      window.removeEventListener('scroll', positionPopup, true);
    };
  }, [isOpen, mapContainerRef, mapRevision, popupContainer]);

  if (cluster) {
    return (
      <button
        onClick={onClick}
        className="group absolute z-1 w-48 h-48 rounded-full flex items-center justify-center -translate-x-1/2 -translate-y-1/2 bottom-1/2 left-1/2 bg-red"
      >
        <div className="w-full h-full flex items-center justify-center text-white text-14">
          {cluster}
        </div>
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => onClick(isOpen ? null : id)}
        ref={markerRef}
        aria-label={isSeller ? 'Punto HOP, vende envíos' : 'Punto HOP'}
        className="group absolute z-1 w-48 h-48 rounded-full flex items-center justify-center -translate-x-1/2 bottom-10 left-1/2 bg-red"
      >
        {isSeller && (
          <Image
            src="/images/puntos-para-envios/sello-vende-envios.svg"
            alt=""
            aria-hidden="true"
            width={88}
            height={40}
            className="absolute z-10 -top-[16px] left-[30px] h-[40px] w-[88px] max-w-none pointer-events-none"
          />
        )}
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="26" fill="none" viewBox="0 0 22 26">
          <g clipPath="url(#clip-envios)">
            <path
              fill="#22CDC6"
              d="M21.567 4.38a.798.798 0 00-.21-.389c-.015-.015-.032-.028-.048-.042h-.002l-.716-.598-1.986-1.656L17.006.367a.451.451 0 00-.37-.058L.977 4.194a.74.74 0 00-.398.276.796.796 0 00-.167.475v15.89c0 .167.063.304.165.388l4.947 4.121a.62.62 0 00.07.048.459.459 0 00.329.031l15.09-3.743a.624.624 0 00.15-.06.819.819 0 00.416-.692v-3.487s.005.01.01.015V4.569a.797.797 0 00-.022-.189zm-9.71-1.658l4.44-1.103a.197.197 0 01.174.034l2.158 1.8.68.566.36.298c.077.066.022.22-.086.246L15.14 5.666a.185.185 0 01-.172-.035L11.77 2.968c-.076-.064-.022-.22.086-.246zM5.758 7.916l-3.2-2.663c-.076-.064-.022-.22.088-.246l4.425-1.098a.185.185 0 01.173.034l2.84 2.365.36.298c.076.066.022.22-.086.247L5.929 7.95a.183.183 0 01-.17-.035zm14.684 12.416c0 .145-.108.29-.243.324l-4.57 1.133c-.133.033-.243-.057-.243-.202V17.11c0-.147-.109-.237-.244-.203l-3.683.916a.34.34 0 00-.243.321v4.476c0 .145-.107.29-.243.323l-4.57 1.134c-.133.033-.243-.058-.243-.202V9.384c0-.144.11-.29.243-.323l4.57-1.134c.136-.033.243.057.243.204v4.486c0 .147.11.237.243.203l3.683-.912a.35.35 0 00.244-.325V7.096c0-.146.11-.291.243-.324l.348-.086.171-.043.025-.005.659-.163 3.367-.836c.136-.034.243.056.243.202v14.49z"
            />
            <path
              fill="#FEFEFE"
              d="M20.443 5.841v14.49c0 .146-.108.29-.243.325l-4.57 1.133c-.134.033-.243-.057-.243-.202V17.11c0-.147-.11-.237-.244-.203l-3.683.916a.34.34 0 00-.243.321v4.476c0 .145-.108.29-.243.323l-4.57 1.134c-.134.033-.243-.058-.243-.202V9.384c0-.144.11-.29.243-.323l4.57-1.134c.135-.033.243.057.243.204v4.486c0 .147.11.237.243.203l3.683-.912a.35.35 0 00.244-.325V7.096c0-.146.11-.291.243-.324l.348-.086.17-.043.025-.005.66-.163 3.367-.836c.135-.034.243.056.243.202zM19.583 4.563L15.14 5.666a.185.185 0 01-.172-.035L11.77 2.968c-.076-.064-.022-.22.086-.246l4.442-1.103a.197.197 0 01.173.034l2.158 1.8.68.566.36.298c.077.066.022.22-.086.246zM10.359 6.853L5.929 7.95a.183.183 0 01-.17-.035l-3.2-2.663c-.076-.064-.022-.22.087-.246l4.426-1.098a.185.185 0 01.173.034l2.84 2.365.36.298c.076.066.021.22-.086.247z"
            />
          </g>
          <defs>
            <clipPath id="clip-envios">
              <path fill="#fff" d="M0 0H21.177V25.147H0z" transform="translate(.412 .294)" />
            </clipPath>
          </defs>
        </svg>
        <div
          className={`absolute top-[96%] left-1/2 -translate-x-1/2 w-16 h-8 bg-red ${markerStyles.triangleClipPath}`}
        />
      </button>

      {isOpen && typeof document !== 'undefined' && createPortal(
        <div
          ref={popupRef}
          role="dialog"
          aria-label={data.name}
          style={{
            position: 'fixed',
            left: popupPosition?.left ?? 0,
            top: popupPosition?.top ?? 0,
            width: popupPosition?.width ?? 'min(320px, calc(100vw - 16px))',
            maxHeight: popupPosition?.maxHeight ?? '70vh',
            visibility: popupPosition ? 'visible' : 'hidden'
          }}
          className="fixed z-[1000] w-[320px] max-w-[calc(100vw-16px)] overflow-y-auto shadow-[0px_24px_26px_0px_rgba(206,206,206,0.36)] bg-white py-14 px-22 rounded-[.8rem]"
        >
          <div className="flex items-start justify-between gap-8 mb-15">
            <div className="min-w-0">
              <p className="p1 !text-12 sm:!text-16 !font-semibold text-black capitalize">{data.name}</p>
              <p className="p1 !text-11 sm:!text-14 text-cyan-dark capitalize">
                {data.street} {data.door_number}
              </p>
              {data.observations && (
                <p className="p1 !text-11 sm:!text-14 text-cyan-dark capitalize">{data.observations}</p>
              )}
            </div>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onClick(null);
              }}
              aria-label="Cerrar detalles del punto"
              title="Cerrar"
              className="flex h-32 w-32 shrink-0 items-center justify-center rounded-full bg-cyan-light text-cyan-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-dark"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="mb-15">
            <p className="p1 !text-10 !font-semibold text-black opacity-30">HORARIOS</p>
            {groupSchedules(data.schedules || []).map((schedule) => (
              <div
                key={schedule.day_description}
                className="p1 !text-12 text-black flex justify-between gap-8"
              >
                <span>{schedule.day_description}</span>
                <span className="text-right">
                  {schedule.intervals.map((interval) => (
                    <strong key={interval} className="block">
                      {interval}
                    </strong>
                  ))}
                </span>
              </div>
            ))}
          </div>
          <ServiceBadges point={data} className="mb-15" />
          <div className="flex items-center justify-between mb-12">
            <div className="mr-50">
              <p className="p1 !text-10 !font-semibold text-black opacity-30">C. POSTAL</p>
              <p className="p1 !text-12 sm:!text-16 text-black">{data.zip_code}</p>
            </div>
            <div>
              <p className="p1 !text-10 !font-semibold text-black opacity-30">Punto HOP</p>
              <p className="p1 !text-12 sm:!text-16 text-black">{data.agency_code}</p>
            </div>
          </div>
          <ComoLlegarCta
            punto={data}
            source="popup"
            searchMethod={searchMethod}
            className="w-full"
          />
        </div>,
        popupContainer || document.body
      )}
    </div>
  );
};

const MapLoading = () => (
  <div
    role="status"
    aria-live="polite"
    className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-12 bg-cyan-light text-center"
  >
    <span className="relative flex h-72 w-72 items-center justify-center" aria-hidden="true">
      <span className="absolute inset-0 rounded-full bg-white/70 motion-safe:animate-ping" />
      <span className="relative flex h-56 w-56 items-center justify-center rounded-full bg-white shadow-lg">
        <MapPinIcon className="h-36 w-36 text-red" />
      </span>
    </span>
    <div className="px-16">
      <p className="p2 !font-semibold text-cyan-dark">Cargando el mapa</p>
      <p className="p3 mt-4 text-cyan-dark opacity-70">Ya vas a ver los puntos HOP</p>
    </div>
  </div>
);

/**
 * Mapa para /conoce-los-puntos-hop. Combina pickup y dropOff de puntos con reseller.
 * Incluye clustering, geolocation y popup con CTA "Cómo llegar".
 *
 * @param {{ pickup: object[], dropOff: object[] }} puntos
 * @param {{ lat: number, lng: number } | null} address
 * @param {{ lat: number, lng: number } | null} userPosition
 * @param {('geolocalizacion'|'direccion')} searchMethod
 * @param {Object} dict
 * @param {Function} onMarkerOpen
 */
const Map = ({
  puntos,
  address,
  userPosition,
  searchMethod,
  dict,
  resetView,
  focusPoint,
  onMarkerOpen,
  fitPointsToBounds = true,
  debugSellerBadge = false
}) => {
  const googleMapsKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY || env('GOOGLE_MAPS_KEY');
  const [mapInstance, setMapInstance] = useState(null);
  const [openMarker, setOpenMarker] = useState(null);
  const [fullscreenElement, setFullscreenElement] = useState(null);
  const [mapBounds, setMapBounds] = useState({ bounds: [0, 0, 0, 0], zoom: 0 });
  const mapContainerRef = useRef(null);

  useEffect(() => {
    const handleFullscreenChange = () => setFullscreenElement(document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    handleFullscreenChange();
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const defaultProps = {
    center: { lat: dict.map.lat, lng: dict.map.lng },
    zoom: dict?.map?.zoom || 13
  };
  const origin = userPosition || address || (resetView?.useLocation ? resetView.center : null);
  const isUserLocation = Boolean(userPosition || resetView?.useLocation);

  const allPoints = useMemo(() => {
    const pickup = puntos?.pickup || [];
    const dropOff = puntos?.dropOff || [];
    const seen = new Set();
    const merged = [];
    for (const p of [...pickup, ...dropOff]) {
      const key = p.id || JSON.stringify(p);
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(p);
      }
    }
    const markerPoints = debugSellerBadge
      ? merged.map((point, index) =>
          index === 0 && !hasSeller(point) ? { ...point, reseller: 'demo' } : point
        )
      : merged;
    return pointsToClusters(markerPoints);
  }, [puntos, debugSellerBadge]);

  const { clusters } = useSupercluster({
    points: allPoints,
    bounds: mapBounds.bounds,
    zoom: mapBounds.zoom,
    options: { radius: 100, maxZoom: 20 }
  });

  // Recenter on address change. Places ya devuelve lat/lng: no hace falta geocodificar.
  useEffect(() => {
    if (address && mapInstance) {
      mapInstance.setCenter(address);
      mapInstance.setZoom(14);
    }
  }, [address, mapInstance]);

  // Recenter on userPosition
  useEffect(() => {
    if (userPosition && mapInstance) {
      mapInstance.setCenter(userPosition);
      mapInstance.setZoom(14);
    }
  }, [userPosition, mapInstance]);

  useEffect(() => {
    if (!resetView || !mapInstance) return;
    mapInstance.setCenter(resetView.center);
    mapInstance.setZoom(resetView.zoom);
    setOpenMarker(null);
  }, [resetView, mapInstance]);

  useEffect(() => {
    if (!focusPoint || !mapInstance) return;
    mapInstance.setCenter(focusPoint);
    mapInstance.setZoom(14);
    setOpenMarker(null);
  }, [focusPoint, mapInstance]);

  const onMapChange = ({ bounds, zoom }) => {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    setMapBounds({ bounds: [sw.lng(), sw.lat(), ne.lng(), ne.lat()], zoom });
  };

  const onClusterclick = (lat, lng) => {
    mapInstance.setCenter({ lat, lng });
    mapInstance.setZoom(mapBounds.zoom + 1);
    setOpenMarker(null);
  };

  const onGoogleApiLoaded = (e) => {
    if (fitPointsToBounds) {
      const validPoints = allPoints.filter((point) =>
        point.geometry.coordinates.every(Number.isFinite)
      );
      if (validPoints.length > 0) {
        const bounds = new e.maps.LatLngBounds();
        validPoints.forEach((point) => {
          const [longitude, latitude] = point.geometry.coordinates;
          bounds.extend({ lat: latitude, lng: longitude });
        });
        e.map.fitBounds(bounds);
      }
    }
    setMapInstance(e.map);
  };

  const handleMarkerClick = (id) => {
    setOpenMarker(id);
    onMarkerOpen?.(id);
  };

  return (
    <div ref={mapContainerRef} className="absolute top-0 left-0 w-full h-full">
      <GoogleMap
        key={googleMapsKey}
        apiKey={googleMapsKey}
        loadingContent={<MapLoading />}
        defaultCenter={{ lat: defaultProps.center.lat, lng: defaultProps.center.lng }}
        defaultZoom={defaultProps.zoom}
        options={{ styles: mapStyle, ...mapOptions }}
        onChange={onMapChange}
        onGoogleApiLoaded={onGoogleApiLoaded}
      >
        {origin && (
          <div
            lat={origin.lat}
            lng={origin.lng}
            role="img"
            aria-label={isUserLocation ? 'Tu ubicación actual' : 'Dirección buscada'}
            title={isUserLocation ? 'Tu ubicación actual' : 'Dirección buscada'}
            className="relative z-2 -translate-x-1/2 -translate-y-1/2"
          >
            <PersonalPointer />
          </div>
        )}

        {(clusters || []).map((cluster) => {
          const [longitude, latitude] = cluster.geometry.coordinates;
          const { cluster: isCluster, point_count: pointCount, punto } = cluster.properties;

          if (!isCluster && !punto) return null;

          return (
            <EnviosMarker
              key={isCluster ? `cluster-${cluster.id ?? pointCount}` : punto.id}
              lat={latitude}
              lng={longitude}
              id={isCluster ? undefined : punto.id}
              cluster={isCluster ? pointCount : undefined}
              openMarker={openMarker}
              onClick={isCluster ? () => onClusterclick(latitude, longitude) : handleMarkerClick}
              data={punto}
              searchMethod={searchMethod}
              mapContainerRef={mapContainerRef}
              mapRevision={mapBounds}
              popupContainer={fullscreenElement}
            />
          );
        })}
      </GoogleMap>
    </div>
  );
};

export default Map;